import React, { useEffect, useRef } from 'react';

interface BoardModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}

/**
 * Modal scaffold matching the BoardView "Delete Collection" modal pattern exactly.
 * Use this for every create/edit/confirm pop-up in the app.
 */
export default function BoardModal({ open, title, onClose, children, footer, width = 480 }: BoardModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Close on Escape + simple focus trap (focus the card on open).
  useEffect(() => {
    if (!open) return;
    cardRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 250,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
        style={{
          width, maxWidth: '92vw', maxHeight: '92vh', overflowY: 'auto',
          background: '#fff', borderRadius: 10,
          boxShadow: '0 14px 40px rgba(0,0,0,0.22)',
          padding: 24, outline: 'none',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: '#0F1117', marginBottom: 16 }}>
          {title}
        </div>
        <div style={{ fontSize: 14, color: '#5A6272' }}>
          {children}
        </div>
        {footer && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Buttons matching the spec ────────────────────────────────────────
const BTN_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontSize: 13, fontWeight: 600,
  padding: '8px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'background 0.12s',
};

export function BMCancelButton({ onClick, disabled, children = 'Cancel' }: { onClick: () => void; disabled?: boolean; children?: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        background: 'transparent',
        color: '#5A6272',
        border: '1px solid #DDE0E6',
        opacity: disabled ? 0.6 : 1,
      }}
      onMouseEnter={e => !disabled && (e.currentTarget.style.background = '#F5F6F8')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >{children}</button>
  );
}

export function BMPrimaryButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        background: '#0E7490',
        color: '#fff',
        border: 'none',
        opacity: disabled ? 0.6 : 1,
      }}
      onMouseEnter={e => !disabled && (e.currentTarget.style.background = '#0A5568')}
      onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
    >{children}</button>
  );
}

export function BMDangerButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        background: '#DC2626',
        color: '#fff',
        border: 'none',
        opacity: disabled ? 0.6 : 1,
      }}
      onMouseEnter={e => !disabled && (e.currentTarget.style.background = '#991B1B')}
      onMouseLeave={e => (e.currentTarget.style.background = '#DC2626')}
    >{children}</button>
  );
}

export function BMSuccessButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        background: '#059669',
        color: '#fff',
        border: 'none',
        opacity: disabled ? 0.6 : 1,
      }}
      onMouseEnter={e => !disabled && (e.currentTarget.style.background = '#047857')}
      onMouseLeave={e => (e.currentTarget.style.background = '#059669')}
    >{children}</button>
  );
}

// ── Inputs / labels ──────────────────────────────────────────────────
export const BM_LABEL: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-ui)',
  fontSize: 13, fontWeight: 500,
  color: '#5A6272', marginBottom: 6,
};

export const BM_INPUT: React.CSSProperties = {
  width: '100%',
  border: '1px solid #DDE0E6',
  borderRadius: 6,
  padding: '8px 12px',
  fontFamily: 'var(--font-ui)',
  fontSize: 14,
  color: '#0F1117',
  background: '#fff',
  outline: 'none',
};

// Prominent title input for the primary identifying field of a modal
// (e.g. task Title, issue Title, risk Title). Larger, display font, visually
// dominant above the secondary form fields. Standardized April 2026 across
// every modal — never use plain BM_INPUT for the title/name field.
export const BM_TITLE_INPUT: React.CSSProperties = {
  ...BM_INPUT,
  fontFamily: 'var(--font-display)',
  fontSize: 22,
  fontWeight: 500,
  padding: '12px 16px',
  letterSpacing: '0.01em',
};

export const BM_TEXTAREA: React.CSSProperties = {
  ...BM_INPUT,
  minHeight: 80,
  resize: 'vertical',
  fontFamily: 'var(--font-ui)',
};
